if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.stockProduct = document.querySelector('[js-product-input]');
        // prefer drawer (if present), otherwise fallback to notification
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.condenseProductsData = document.querySelector('[js-upsell-condense]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.quantitySelected = 1;
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /* ----------------- Condense / override helpers ----------------- */

      recursivelyCondenseItems(items, config){
        let isItemsCondensed = false;

        config.forEach(condenseItem => {
          let fromIds = condenseItem.from.replaceAll(' ', '').split(',').map(id => id.trim());
          let toId = condenseItem.to.toString();

          const hasAllKeys = fromIds.every(fromId => items.some(item => item.id.toString() === fromId));
          if (hasAllKeys) {
            fromIds.forEach(fromId => {
              const index = items.findIndex(item => item.id.toString() === fromId);
              if (index > -1) items.splice(index, 1);
            });
            items.push({ id: toId, quantity: this.quantitySelected });
            isItemsCondensed = true;
          }
        });

        if (isItemsCondensed) return this.recursivelyCondenseItems(items, config);
        return items;
      }

      condenseProducts(items){
        try {
          const config = JSON.parse(this.condenseProductsData?.textContent || '{}')?.condense_to_one_item;
          if (!config) return items;
          let itemsToCondense = [...items];
          return this.recursivelyCondenseItems(itemsToCondense, config);
        } catch (error) {
          console.error('Error condensing items:', error);
          return items;
        }
      }

      overrideProducts(formData){
        let { items } = formData;
        let newFormData = formData;
        let updatedItems = [];

        // Hard-coded ID values as per old functionality
        const SUPPORT_SEAT_ID = '40170740777038';
        const TRIKE_CARGO_ID = '40186017808462';
        const TRIKE_CARGO_AND_SEAT = '40034954903630';

        const supportSeatItem = items.find(item => item.id === SUPPORT_SEAT_ID);
        const trikeCargoItem = items.find(item => item.id === TRIKE_CARGO_ID);

        if (supportSeatItem && trikeCargoItem) {
          updatedItems.push({ id: TRIKE_CARGO_AND_SEAT, quantity: this.quantitySelected });
          updatedItems = updatedItems.concat(items.filter(item => item.id !== SUPPORT_SEAT_ID && item.id !== TRIKE_CARGO_ID));
        } else {
          updatedItems = items;
        }

        if (this.condenseProductsData) {
          newFormData.items = this.condenseProducts(updatedItems);
        } else {
          newFormData.items = updatedItems;
        }

        return newFormData;
      }

      buildFormData(){
        this.stockProduct && (this.stockProduct.checked = true);
        const productContainer = this.closest('product-info');

        const originalFormData = new FormData(this.form);
        const quantity = originalFormData.get('quantity');
        this.quantitySelected = quantity;

        let updatedFormData = { items: [] };

        if (productContainer) {
          const upsellItems = document.querySelectorAll('input.upsell-item-checkbox');
          const selectedItems = upsellItems ? Array.from(upsellItems).filter(item => item.checked) : [];

          for (let i = 0; i < selectedItems.length; i++) {
            const upsellItem = selectedItems[i];
            const productId = upsellItem.getAttribute('data-product-id');

            const excludeStockBike = upsellItem.getAttribute('data-exclude-stock-bike') === 'true';
            if (excludeStockBike && this.stockProduct) this.stockProduct.checked = false;

            updatedFormData.items.push({ id: productId, quantity });
          }
        }

        if (updatedFormData.items.length > 0) {
          const sections = originalFormData.get('section-id');
          if (sections) updatedFormData.sections = sections;

          if (this.stockProduct?.checked) {
            updatedFormData.items.push({
              id: this.stockProduct.getAttribute('data-product-id'),
              quantity: 1
            });
          }

          return { formData: this.overrideProducts(updatedFormData), type: 'JSON' };
        } else {
          return { formData: originalFormData, type: 'Form' };
        }
      }

      /* ----------------- Submit ----------------- */

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        let { formData, type } = this.buildFormData();

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          const sectionConfigs = this.cart.getSectionsToRender() || [];
          const sectionsToRender = Array.from(new Set(sectionConfigs.map(s => s.section).filter(Boolean)));

          if (type === 'JSON') {
            // (use CSV string for reliability)
            formData.sections = sectionsToRender.join(',');
            formData.sections_url = window.location.pathname;
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(formData);
          } else {
            formData.append('sections', sectionsToRender.join(','));
            formData.append('sections_url', window.location.pathname);
            delete config.headers['Content-Type'];
            config.body = formData;
          }

          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        } else {
          if (type === 'JSON') {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(formData);
          } else {
            delete config.headers['Content-Type'];
            config.body = formData;
          }
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: type === 'JSON' ? formData.items?.[0]?.id : formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Remember last-added variant for drawer scroll
            const justAddedId = (type === 'JSON' ? (formData.items?.[0]?.id) : formData.get('id')) || '';
            try { window.__lastAddedVariantId = String(justAddedId || ''); } catch(e) {}
            document.dispatchEvent(new CustomEvent('theme:cart:item-added', { detail: { variantId: justAddedId }}));

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: justAddedId,
                cartData: response,
              });
            }
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    // tell other tabs and render
                    try { localStorage.setItem('cart:dirty', String(Date.now())); } catch(e) {}

                    // Ensure the drawer is open before rendering new contents
                    if (this.cart && typeof this.cart.open === 'function' && !this.cart.classList.contains('active')) {
                      this.cart.open(this.submitButton || document.activeElement || this);
                    }

                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // let other tabs know the cart changed & render drawer
              try { localStorage.setItem('cart:dirty', String(Date.now())); } catch(e) {}

              // *** INSERT NEW CODE HERE *** (open the drawer immediately)
              if (this.cart && typeof this.cart.open === 'function') this.cart.open();

              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || document.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.classList.toggle('hidden', !errorMessage);
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
